import { FilterUtil } from '@shared/utils/filter.util';
import { PREFIX } from './i18n.service';
import { I18nServiceConfig } from './models-external';
import { CIMODE } from './models-internal';

/**
 * Apply defaults to a configuration.
 *
 * @returns The given configuration modified with defaults.
 */
export function applyConfigDefaults(config: I18nServiceConfig): Required<I18nServiceConfig> {
  return {
    ...config,
    fallbackLang: config.fallbackLang ?? CIMODE,
    debug: config.debug ?? false,
  }
}

/**
 * Validate the language codes in a given configuration.
 */
export function validateLanguageCodes(config: I18nServiceConfig) {
  const { resources, fallbackLang, initialLang } = config;
  const languageCodes = Object.keys(resources);

  if (![...languageCodes, CIMODE].includes(fallbackLang)) {
    console.error(PREFIX, 'Fallback should be a language in the resources list (or "cimode")');
  }

  if (![...languageCodes, CIMODE].includes(initialLang)) {
    console.error(PREFIX, 'Initial language should be defined, and should be a language in the resources list (or "cimode")');
  }

  if (languageCodes.includes(CIMODE)) {
    console.error(PREFIX, `Not allowed to specify config for the debug language ("${CIMODE}")`);
  }
}

/**
 * Normalize a list of language codes.
 *
 * This guarantees the following:
 *
 * 1. The list contains only truthy strings.
 *
 *    This means it filters out any unspecified/undefined entries.
 *
 * 2. Converts to lowercase.
 *
 *    This is so that you don't have to care if a language code is uppercase or lowercase.
 *
 * 2. If a langauge code contains a locale, we search the specific then general form.
 *
 *    Example: if `en-GB` is specified, we'll search `en-gb` then `en`.
 *
 * 3. Duplicate language codes are removed, preferring the earliest possible point of searching for it.
 *
 *    Example: `[en-GB, fr, en, cimode]` initially turns into `[en-gb, en, fr, en, cimode]`, then de-duplicates into `[en-gb, en, fr, cimode]`.
 *    This means we don't try to find `en` twice, while preserving that we search for `en` before `fr`.
 *
 * @param languageCodes A list of language codes
 * @returns A normalized list of language codes.
 */
export function normalizeLanguageCodes(languageCodes: (string | undefined)[]): string[] {
  return languageCodes
    .filter(Boolean)
    .map((code) => {
      if (code.includes('-')) {
        // split 'en-GB' into 'en-GB' then 'en'
        const lang = code.split('-')[0];
        return [code, lang];
      } else {
        return [code];
      }
    })
    .flat()
    .map(code => code.toLowerCase())
    .filter(FilterUtil.unique);
}
